import { useState, useCallback, useRef, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import client, { signUpload } from "@culturehq/client";

import getText from "components/editor/getText";
import { t } from "app/utils";

const useLandingPage = ({
  landingPage,
  onSubmitted
}) => {
  const isMounted = useRef(true);
  const editorRef = useRef(null);
  const history = useHistory();
  const { id: landingPageId } = useParams();
  const [approvingContent, setApprovingContent] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStories, setSelectedStories] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [changed, setChanged] = useState(false);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [valuesLocal, setValuesLocal] = useState({
    logoUrl: landingPage?.logoUrl,
    headerImageUrl: landingPage?.headerImageUrl,
    headerText: landingPage?.headerText || "",
    route: landingPage?.route || "",
    welcomeNote: landingPage?.welcomeNote || "",
    primaryColor: landingPage?.primaryColor || "#79b17d",
    name: landingPage?.name || ""
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const goToLandingPages = () => {
    history.push("/admin/landing-pages");
  };

  const getResponse = useCallback(
    async (logo, headerImage, headerText, name, welcomeNote, primaryColor, live, route) => {
      let createdPayload;
      try {
        if (landingPageId === "new") {
          createdPayload = {
            logoUrl: logo,
            headerImageUrl: headerImage,
            headerText,
            name,
            welcomeNote,
            primaryColor,
            live,
            route,
            users: selectedUsers,
            stories: selectedStories
          };
          await client.createLandingPage(createdPayload);
        } else {
          await client.updateLandingPage({
            landingPageId: landingPage.id,
            logoUrl: logo,
            name,
            headerImageUrl: headerImage,
            headerText,
            welcomeNote,
            primaryColor,
            live,
            route
          });
        }
        onSubmitted();
        goToLandingPages();
      } catch (err) {
        setError(err);
        setLoading(false);
        onSubmitted(createdPayload || landingPage);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landingPage, onSubmitted, selectedUsers, selectedStories]
  );

  const onAWSUploadedFile = useCallback(
    async (logo, headerImage, headerText, name, welcomeNote, primaryColor, live, route) => {
      await getResponse(
        logo, headerImage, headerText, name, welcomeNote, primaryColor, live, route
      );
    },
    [getResponse]
  );

  const waitForS3 = useCallback(
    async (ms, logo, headerImage, headerText, name, welcomeNote, primaryColor, live, route) => (
      new Promise(_ => {
        // This timeout is to allow AWS to upload the file, in this point,
        // AWs has responded with a 200 status, but we need to take care of
        // S3 Consistency Model
        setTimeout(
          onAWSUploadedFile,
          ms,
          logo,
          headerImage,
          headerText,
          name,
          welcomeNote,
          primaryColor,
          live,
          route
        );
      })
    ),
    [onAWSUploadedFile]
  );

  const handleSubmit = useCallback(
    async ({ logo, headerImage, headerText, name, route }) => {
      if (!isReady) return;

      const welcomeNote = await editorRef.current.serialize();
      if (getText(welcomeNote).length > 0) {
        setError();
        if (!approvingContent) {
          setLoading(true);
          const headerImageUrl = headerImage
            ? await signUpload(headerImage)
            : landingPage.headerImageUrl;
          const logoUrl = logo
            ? await signUpload(logo)
            : landingPage.logoUrl;
          await waitForS3(
            2000,
            logoUrl,
            headerImageUrl,
            headerText || valuesLocal.headerText,
            name || valuesLocal.name,
            welcomeNote,
            valuesLocal.primaryColor,
            isLive,
            route || valuesLocal.route
          );
        }
      } else {
        setError(t("The welcome note is a required field"));
      }
    },
    [approvingContent, waitForS3, valuesLocal, landingPage, isLive, isReady]
  );

  const handleIsLive = async isLive => {
    if (!landingPage && !isReady) return;

    setLoading(true);
    const welcomeNote = editorRef.current
      ? await editorRef.current.serialize() : valuesLocal.welcomeNote;
    const headerImageUrl = valuesLocal.headerImageUrl && typeof valuesLocal.headerImageUrl !== "string"
      ? await signUpload(valuesLocal.headerImageUrl)
      : landingPage.headerImageUrl;
    const logoUrl = valuesLocal.logoUrl && typeof valuesLocal.logoUrl !== "string"
      ? await signUpload(valuesLocal.logoUrl)
      : landingPage.logoUrl;
    const name = valuesLocal.name || landingPage.name;
    await waitForS3(
      2000,
      logoUrl,
      headerImageUrl,
      valuesLocal.headerText,
      name,
      welcomeNote,
      valuesLocal.primaryColor,
      isLive,
      valuesLocal.route
    );
  };

  return {
    editorRef,
    loading,
    isLive,
    isReady,
    changed,
    error,
    valuesLocal,
    setIsLive,
    setIsReady,
    setChanged,
    setValuesLocal,
    setApprovingContent,
    setSelectedUsers,
    setSelectedStories,
    handleSubmit,
    handleIsLive
  };
};

export default useLandingPage;
